import React, {
    useState,
} from 'react';
import {
    NSCard, NSDropdown, NSFormGroup, NSInput, NSLabel,
} from 'bricks';
import { IOptionAnyValue } from 'bricks/types';
import { SpecsData } from 'views/InvestmentMemo/types';
import { AssetTypeIds } from 'components/DealForm/constants';
import RenderIf from 'components/RenderIf/RenderIf';
import {
    STRUCTURED_PARKING,
    SURFACE_PARKING,
    UNDERGROUND_PARKING,
    TRAILER_PARKING,
} from 'views/ProFormaTable/constants';
import './Specs.scss';
import { useSpecsLoader } from './useSpecsLoader';
import { useInvestmentMemoContext } from '../../../../InvestmentMemoContext';

interface ISpecsProps {
    specs: SpecsData;
    onChange: (data: Partial<SpecsData>) => void;
}

const Specs = ({ specs, onChange }: ISpecsProps) => {
    const { shouldShowError, getFieldError } = useInvestmentMemoContext();
    const {
        assetTypeOptions,
        loadingStates,
        getSubTypeOptions,
        initialSubTypeOptions,
        parkingOptionsForModel,
    } = useSpecsLoader(specs);

    const [subTypeOptions, setSubTypeOptions] = useState<IOptionAnyValue<string>[]>(initialSubTypeOptions);

    const updateParkingSpaces = (newParkingSpaces: SpecsData['parkingSpaces']) => {
        onChange({
            parkingSpaces: newParkingSpaces,
        });
    };

    const shouldShowParkingType = (parkingType: string) => parkingOptionsForModel.some(option => option.value === parkingType);

    return (
        <NSFormGroup>
            <NSCard className="Specs NSCard--level-3 p-3">
                <NSLabel>The specs</NSLabel>
                <div className="Specs__field-col d-flex flex-column">
                    <div className="Specs__field-row d-flex flex-column flex-md-row flex-md-wrap">
                        <div className="flex-fill">
                            <NSLabel className="text-dark font-weight-normal">
                                Asset class
                                <span className="text-danger"> *</span>
                            </NSLabel>
                            <NSDropdown
                                toggleClassName="Specs__input"
                                options={assetTypeOptions}
                                selectedOption={assetTypeOptions.find(
                                    o => o.value === (specs?.assetType?.correlationId || AssetTypeIds.RentalApartment),
                                )}
                                handleSelectOption={option => {
                                    const newSubTypeOptions = option?.value ? getSubTypeOptions(option.value) : [];
                                    setSubTypeOptions(newSubTypeOptions);
                                    onChange({
                                        assetType: option ? { correlationId: option.value, name: option.label } : undefined,
                                        assetSubtype: newSubTypeOptions.length > 0
                                            ? { correlationId: newSubTypeOptions[0].value, name: newSubTypeOptions[0].label }
                                            : undefined,
                                    });
                                }}
                                isFullWidth
                                disabled={loadingStates.assetTypes}
                            />
                            {shouldShowError('dealOverview.specs.assetType') && (
                                <p className="text-danger small mt-1 mb-0">{getFieldError('dealOverview.specs.assetType')}</p>
                            )}
                        </div>
                        <RenderIf isTrue={subTypeOptions.length > 0}>
                            <div className="flex-fill">
                                <NSLabel className="text-dark font-weight-normal">
                                    Subtype
                                    <span className="text-danger"> *</span>
                                </NSLabel>
                                <NSDropdown
                                    toggleClassName="Specs__input"
                                    options={subTypeOptions}
                                    selectedOption={subTypeOptions.find(
                                        o => o.value === specs?.assetSubtype?.correlationId,
                                    )}
                                    handleSelectOption={option => {
                                        onChange({
                                            assetSubtype: option ? { correlationId: option.value, name: option.label } : undefined,
                                        });
                                    }}
                                    isFullWidth
                                    disabled={loadingStates.assetTypes}
                                />
                                {shouldShowError('dealOverview.specs.assetSubtype') && (
                                    <p className="text-danger small mt-1 mb-0">{getFieldError('dealOverview.specs.assetSubtype')}</p>
                                )}
                            </div>
                        </RenderIf>

                        <div className="flex-fill">
                            <NSLabel className="text-dark font-weight-normal">
                                Site area
                                <span className="text-danger"> *</span>
                            </NSLabel>
                            <NSInput
                                type="number"
                                name="siteArea"
                                customClassName="Specs__input"
                                inputGroupClassName="p-0"
                                value={specs.siteArea}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    onChange({
                                        siteArea: e.target.valueAsNumber,
                                    });
                                }}
                                showErrorMsg={shouldShowError('dealOverview.specs.siteArea')}
                                errorMsg={getFieldError('dealOverview.specs.siteArea')}
                                appendInputAddon={<div className="px-2">acres</div>}
                            />
                        </div>
                    </div>

                    <div className="Specs__field-row d-flex flex-column flex-md-row flex-md-wrap">
                        <div className="flex-fill">
                            <NSLabel className="text-dark font-weight-normal">
                                Building size
                                <span className="text-danger"> *</span>
                            </NSLabel>
                            <div className="Specs__field-row d-flex flex-row">
                                <NSInput
                                    type="number"
                                    name="buildingSizeGrossSF"
                                    customClassName="Specs__input"
                                    inputGroupClassName="p-0"
                                    value={specs.buildingSizeGrossSF}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        onChange({
                                            buildingSizeGrossSF: e.target.valueAsNumber,
                                        });
                                    }}
                                    showErrorMsg={shouldShowError('dealOverview.specs.buildingSizeGrossSF')}
                                    errorMsg={getFieldError('dealOverview.specs.buildingSizeGrossSF')}
                                    appendInputAddon={<div className="px-2">gross ft²</div>}
                                />
                            </div>
                        </div>
                        <div className="flex-fill">
                            <NSLabel className="text-dark font-weight-normal">&nbsp;</NSLabel>
                            <NSInput
                                type="number"
                                name="buildingSizeNetRentableSF"
                                customClassName="Specs__input"
                                inputGroupClassName="p-0"
                                value={specs.buildingSizeNetRentableSF}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    onChange({
                                        buildingSizeNetRentableSF: e.target.valueAsNumber,
                                    });
                                }}
                                showErrorMsg={shouldShowError('dealOverview.specs.buildingSizeNetRentableSF')}
                                errorMsg={getFieldError('dealOverview.specs.buildingSizeNetRentableSF')}
                                appendInputAddon={<div className="px-2">net rentable ft²</div>}
                            />

                        </div>
                    </div>
                    <div className="Specs__field-row d-flex justify-content-between">
                        <RenderIf isTrue={shouldShowParkingType(STRUCTURED_PARKING) && !!specs.parkingSpaces.structured}>
                            <div className="d-flex flex-column flex-fill">
                                <NSLabel className="text-nowrap text-dark font-weight-normal">Structured parking</NSLabel>
                                <NSInput
                                    type="number"
                                    name="structuredParkingSpaces"
                                    value={specs.parkingSpaces.structured ?? 0}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const value = Number.isNaN(e.target.valueAsNumber) ? null : e.target.valueAsNumber;
                                        updateParkingSpaces({ ...specs.parkingSpaces, structured: value });
                                    }}
                                />
                            </div>
                        </RenderIf>

                        <RenderIf isTrue={shouldShowParkingType(SURFACE_PARKING) && !!specs.parkingSpaces.surface}>
                            <div className="d-flex flex-column flex-fill">
                                <NSLabel className="text-nowrap text-dark font-weight-normal">Surface parking</NSLabel>
                                <NSInput
                                    type="number"
                                    name="surfaceParkingSpaces"
                                    value={specs.parkingSpaces.surface ?? 0}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const value = Number.isNaN(e.target.valueAsNumber) ? null : e.target.valueAsNumber;
                                        updateParkingSpaces({ ...specs.parkingSpaces, surface: value });
                                    }}
                                />
                            </div>
                        </RenderIf>

                        <RenderIf isTrue={shouldShowParkingType(UNDERGROUND_PARKING) && !!specs.parkingSpaces.underground}>
                            <div className="d-flex flex-column flex-fill">
                                <NSLabel className="text-nowrap text-dark font-weight-normal">Underground parking</NSLabel>
                                <NSInput
                                    type="number"
                                    name="undergroundParkingSpaces"
                                    value={specs.parkingSpaces.underground ?? 0}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const value = Number.isNaN(e.target.valueAsNumber) ? null : e.target.valueAsNumber;
                                        updateParkingSpaces({ ...specs.parkingSpaces, underground: value });
                                    }}
                                />
                            </div>
                        </RenderIf>

                        <RenderIf isTrue={shouldShowParkingType(TRAILER_PARKING) && !!specs.parkingSpaces.trailer}>
                            <div className="d-flex flex-column flex-fill">
                                <NSLabel className="text-nowrap text-dark font-weight-normal">Trailer parking</NSLabel>
                                <NSInput
                                    type="number"
                                    name="trailerParkingSpaces"
                                    value={specs.parkingSpaces.trailer ?? 0}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const value = Number.isNaN(e.target.valueAsNumber) ? null : e.target.valueAsNumber;
                                        updateParkingSpaces({ ...specs.parkingSpaces, trailer: value });
                                    }}
                                />
                            </div>
                        </RenderIf>
                    </div>
                </div>
            </NSCard>
        </NSFormGroup>
    );
};

export default Specs;
